import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import {
    CHEAT_SHEET_SCOPE,
    CHEAT_SHEET_SHORTCUT,
    shortcutRegistry,
    type ShortcutDefinition,
    type ShortcutScope,
} from './keymap';
import { useShortcutContext } from './ShortcutProvider';
import { useShortcut } from './useShortcut';
import { useShortcutScope } from './useShortcutScope';
import { ShortcutHint } from './ShortcutHint';

type ShortcutGroup = {
    scope: ShortcutScope;
    entries: ShortcutDefinition[];
};

/** Groups the registry by scope, the given scope's group listed first. */
const groupByScope = (
    entries: ShortcutDefinition[],
    firstScope: ShortcutScope,
): ShortcutGroup[] => {
    const scopes = Array.from(new Set(entries.map((entry) => entry.scope)));
    const ordered = [
        ...(scopes.includes(firstScope) ? [firstScope] : []),
        ...scopes.filter((scope) => scope !== firstScope),
    ];
    return ordered.map((scope) => ({
        scope,
        entries: entries.filter((entry) => entry.scope === scope),
    }));
};

/**
 * Pushed only while the cheat sheet is open, suspending every other scope so
 * no shortcut fires underneath it. Kept a separate component (rather than
 * called unconditionally) so the push/pop tracks `isOpen`.
 */
const CheatSheetScope: React.FC = () => {
    useShortcutScope(CHEAT_SHEET_SCOPE);
    return null;
};

/**
 * `F1`'s cheat sheet, listing every registry entry grouped by scope, the
 * scope active when `F1` opened it listed first — an open dialog's own keys,
 * when `F1` is pressed inside one. `F1` again while it is open changes
 * nothing (the live scope is then its own). Mounted on the seller path only,
 * by the router's `CheatSheetRoute` (sign-in and the sale screen), so it
 * opens from either screen or from inside any open seller dialog; elsewhere
 * `F1` is the browser's. Unlike a seller dialog, it does not override the
 * design system's restore-to-previous-focus behaviour on close.
 *
 * With every key on the seller path listed, it takes the design system's
 * `cover` size and flows its groups into columns as the screen widens. The
 * list is a named region in the Tab order, so a keyboard-only seller can
 * scroll it with the arrows and `PageUp`/`PageDown`; nothing else in it is
 * focusable.
 */
export const CheatSheet: React.FC = () => {
    const { t } = useTranslation('components');
    const { getActiveScope } = useShortcutContext();
    const [isOpen, setIsOpen] = React.useState(false);
    const [openedFromScope, setOpenedFromScope] = React.useState<ShortcutScope>(
        () => getActiveScope(),
    );

    useShortcut(CHEAT_SHEET_SHORTCUT.id, () => {
        if (isOpen) {
            return;
        }
        setOpenedFromScope(getActiveScope());
        setIsOpen(true);
    });

    const groups = React.useMemo(
        () => groupByScope(shortcutRegistry, openedFromScope),
        [openedFromScope],
    );

    return (
        <>
            {isOpen ? <CheatSheetScope /> : null}
            <Modal.Backdrop
                isOpen={isOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setIsOpen(false);
                    }
                }}
                variant="blur"
            >
                <Modal.Container placement="center" size="cover">
                    <Modal.Dialog
                        {...getNodeTestIdProp('shortcut-cheat-sheet')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Icon>
                                <Icon
                                    className="size-5"
                                    icon="solar:keyboard-bold-duotone"
                                />
                            </Modal.Icon>
                            <Modal.Heading>
                                {t(CHEAT_SHEET_SHORTCUT.labelKey)}
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body
                            aria-label={t(
                                'components:shortcuts.cheatSheetList',
                            )}
                            role="region"
                            tabIndex={0}
                        >
                            <div className="gap-8 md:columns-2 xl:columns-3">
                                {groups.map(({ scope, entries }) => (
                                    <div
                                        className="mb-6 break-inside-avoid"
                                        key={scope}
                                    >
                                        <Typography
                                            className="mb-2"
                                            color="muted"
                                            type="body-xs"
                                            weight="semibold"
                                        >
                                            {t(
                                                `components:shortcuts.scope.${scope}`,
                                            )}
                                        </Typography>
                                        <div className="flex flex-col gap-2">
                                            {entries.map((entry) => (
                                                <div
                                                    className="flex items-center justify-between gap-4"
                                                    key={entry.id}
                                                    {...getNodeTestIdProp(
                                                        `shortcut-cheat-sheet-entry-${entry.id}`,
                                                    )}
                                                >
                                                    <Typography type="body-sm">
                                                        {t(entry.labelKey)}
                                                    </Typography>
                                                    <ShortcutHint
                                                        placement="list"
                                                        shortcut={entry}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Modal.Body>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </>
    );
};
